import { IconContext } from 'react-icons';
import {
  FaBars, FaUser, FaTasks, FaSignOutAlt,
  FaBell, FaCog, FaMoneyBillWave, FaChalkboardTeacher,
  FaBookOpen, FaUsers, FaCalendarAlt, FaGamepad,
  FaClipboardList, FaFileAlt, FaFlask, FaChevronDown,
} from 'react-icons/fa';
import { MdAttachMoney, MdDashboard, MdOutlineReviews } from 'react-icons/md';
import { PiStudentBold } from 'react-icons/pi';
import { SiMdbook } from 'react-icons/si';
import { BsFillPatchQuestionFill, BsCollection } from 'react-icons/bs';
import { GiCardPick } from 'react-icons/gi';
import { IoGameController } from 'react-icons/io5';
import { BiBookBookmark } from 'react-icons/bi';
import { HiOutlineDocumentText, HiOutlineViewGrid } from 'react-icons/hi';
import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../hooks';
import { userActions } from '../../redux/actions';
import { useNavigate } from 'react-router-dom';
import MeContainer from './MeContainer';
import { pathConstants } from '../../constants';
import {
  Chevron, Dropdown, DropdownItem, Footer, Logo,
  Nav, NavItem, NavLink, NavSection, Sidebar, ToggleButton,
} from './Header.styled';
import my_avatar from '../../images/logo-kt.png';

interface HeaderProps {
  expand: boolean;
  onExpandChange: (expand: boolean) => void;
}

const Header: React.FC<{ expand: boolean; onExpandChange: (expand: boolean) => void }> = React.memo(({ expand, onExpandChange }) => {
  const handleToggleExpand = () => onExpandChange(!expand);

  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 1024);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 1024);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const toggleDropdown = (menu: string) =>
    setActiveDropdown(activeDropdown === menu ? null : menu);

  const [activePath, setActivePath] = useState<string>('/');
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const userInfo = useAppSelector((state: any) => state.authentication.userInfo);

  const onLogout = () => {
    dispatch(userActions.logout());
    navigate(pathConstants.ROOT);
    if (isMobile) onExpandChange(false);
  };

  useEffect(() => { setActivePath(location.pathname); }, [location.pathname]);

  useEffect(() => {
    const saved = localStorage.getItem('userInfo');
    if (saved) dispatch(userActions.loginSuccess(JSON.parse(saved)));
  }, [dispatch]);

  const handleNavClick = (path: string, e?: React.MouseEvent) => {
    if (e) {
      if (e.button === 1 || e.ctrlKey || e.metaKey) return;
      e.preventDefault();
    }
    setActivePath(path);
    navigate(path);
    if (isMobile) onExpandChange(false);
  };

  const isTeacherOrAdmin = userInfo?.role === 0 || userInfo?.role === 1;
  const isAdmin = userInfo?.role === 0;

  return (
    <IconContext.Provider value={{ size: '1em' }}>
      {/* Mobile overlay */}
      {isMobile && expand && (
        <div
          onClick={() => onExpandChange(false)}
          style={{
            position: 'fixed', inset: 0,
            backgroundColor: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(2px)',
            zIndex: 998,
          }}
        />
      )}

      {/* Mobile hamburger */}
      {isMobile && (
        <ToggleButton onClick={handleToggleExpand}>
          <FaBars />
        </ToggleButton>
      )}

      <Sidebar expand={expand}>
        {/* Brand row */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '0.75rem 0.75rem 0.5rem', gap: 8, flexShrink: 0 }}>
          {!isMobile && (
            <ToggleButton onClick={handleToggleExpand} style={{ position: 'static', width: 40, height: 40, padding: '0.6rem', fontSize: '1.1rem' }}>
              <FaBars />
            </ToggleButton>
          )}
          <Logo expand={expand}>KT English</Logo>
        </div>

        {/* User info */}
        <div style={{ padding: '0 0.5rem 0.25rem', flexShrink: 0 }}>
          <MeContainer avatar={my_avatar} text={expand ? userInfo?.userName : ''} />
        </div>

        {/* Divider */}
        <div style={{ height: 1, background: 'linear-gradient(90deg, transparent, rgba(129,140,248,0.25), transparent)', margin: '0 0.75rem 0.25rem', flexShrink: 0 }} />

        <Nav>
          {/* ── KHÁM PHÁ ───────────────────────────────────────── */}
          <NavSection expand={expand}>Khám phá</NavSection>

          {/* Game */}
          {userInfo && (
            <NavItem>
              <NavLink expand={expand} href="#" onClick={(e) => { e.preventDefault(); toggleDropdown('game'); }}>
                <IoGameController />
                <span>Game</span>
                <Chevron open={activeDropdown === 'game'}><FaChevronDown /></Chevron>
              </NavLink>
              {activeDropdown === 'game' && (
                <Dropdown expand={expand}>
                  <DropdownItem
                    onClick={(e: React.MouseEvent) => handleNavClick(pathConstants.MATCH_WORD_MEANING, e)}
                    style={{
                      color: activePath === pathConstants.MATCH_WORD_MEANING ? '#c4b5fd' : undefined,
                      borderLeftColor: activePath === pathConstants.MATCH_WORD_MEANING ? '#818cf8' : undefined,
                    }}
                  >
                    🔗 Nối từ
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e: React.MouseEvent) => handleNavClick(pathConstants.CLAP_GAME, e)}
                    style={{
                      color: activePath === pathConstants.CLAP_GAME ? '#c4b5fd' : undefined,
                      borderLeftColor: activePath === pathConstants.CLAP_GAME ? '#818cf8' : undefined,
                    }}
                  >
                    👏 Vẫy tay / Lắc đầu
                  </DropdownItem>
                </Dropdown>
              )}
            </NavItem>
          )}


          {/* Chế độ giảng dạy */}
          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink expand={expand} href="#" onClick={(e) => { e.preventDefault(); toggleDropdown('teaching'); }}>
                <FaChalkboardTeacher />
                <span>Chế độ giảng dạy</span>
                <Chevron open={activeDropdown === 'teaching'}><FaChevronDown /></Chevron>
              </NavLink>
              {activeDropdown === 'teaching' && (
                <Dropdown expand={expand}>
                  <DropdownItem
                    onClick={(e: React.MouseEvent) => handleNavClick(pathConstants.SENTENCE_ORDER, e)}
                    style={{
                      color: activePath === pathConstants.SENTENCE_ORDER ? '#bae6fd' : undefined,
                      borderLeftColor: activePath === pathConstants.SENTENCE_ORDER ? '#38bdf8' : undefined,
                    }}
                  >
                    🔡 Sắp xếp câu
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e: React.MouseEvent) => handleNavClick(pathConstants.MULTIPLE_CHOICE, e)}
                    style={{
                      color: activePath === pathConstants.MULTIPLE_CHOICE ? '#bae6fd' : undefined,
                      borderLeftColor: activePath === pathConstants.MULTIPLE_CHOICE ? '#38bdf8' : undefined,
                    }}
                  >
                    🧠 Trắc nghiệm
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e: React.MouseEvent) => handleNavClick(pathConstants.STUDENT_PICKER, e)}
                    style={{
                      color: activePath === pathConstants.STUDENT_PICKER ? '#bae6fd' : undefined,
                      borderLeftColor: activePath === pathConstants.STUDENT_PICKER ? '#38bdf8' : undefined,
                    }}
                  >
                    🎯 Chọn học sinh
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e: React.MouseEvent) => handleNavClick(pathConstants.GIFT_MANAGE, e)}
                    style={{
                      color: activePath === pathConstants.GIFT_MANAGE ? '#bae6fd' : undefined,
                      borderLeftColor: activePath === pathConstants.GIFT_MANAGE ? '#38bdf8' : undefined,
                    }}
                  >
                    🎁 Quản lý quà tặng
                  </DropdownItem>
                </Dropdown>
              )}
            </NavItem>
          )}

          {/* ── QUẢN LÝ ───────────────────────────────────────── */}
          {isTeacherOrAdmin && <NavSection expand={expand}>Quản lý</NavSection>}

          {isAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.CLASSES_MANAGE}
                isActive={activePath === pathConstants.CLASSES_MANAGE}
                onClick={(e) => handleNavClick(pathConstants.CLASSES_MANAGE, e)}
              >
                <FaTasks />
                <span>Lớp học</span>
              </NavLink>
            </NavItem>
          )}

          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.SCHEDULES_MANAGE}
                isActive={activePath === pathConstants.SCHEDULES_MANAGE}
                onClick={(e) => handleNavClick(pathConstants.SCHEDULES_MANAGE, e)}
              >
                <FaCalendarAlt />
                <span>Lịch học</span>
              </NavLink>
            </NavItem>
          )}

          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.STUDENTS_MANAGE}
                isActive={activePath === pathConstants.STUDENTS_MANAGE}
                onClick={(e) => handleNavClick(pathConstants.STUDENTS_MANAGE, e)}
              >
                <PiStudentBold />
                <span>Học sinh</span>
              </NavLink>
            </NavItem>
          )}

          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.ATTENDANCE_MANAGE}
                isActive={activePath === pathConstants.ATTENDANCE_MANAGE}
                onClick={(e) => handleNavClick(pathConstants.ATTENDANCE_MANAGE, e)}
              >
                <FaClipboardList />
                <span>Điểm danh</span>
              </NavLink>
            </NavItem>
          )}

          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href="/review-manage"
                isActive={activePath.startsWith('/review-manage')}
                onClick={(e) => handleNavClick('/review-manage', e)}
              >
                <MdOutlineReviews />
                <span>Ôn tập</span>
              </NavLink>
            </NavItem>
          )}

          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.LESSON_DIARY_MANAGE}
                isActive={activePath === pathConstants.LESSON_DIARY_MANAGE}
                onClick={(e) => handleNavClick(pathConstants.LESSON_DIARY_MANAGE, e)}
              >
                <SiMdbook />
                <span>Nhật kí dạy học</span>
              </NavLink>
            </NavItem>
          )}

          {/* ── NỘI DUNG ──────────────────────────────────────── */}
          {isTeacherOrAdmin && <NavSection expand={expand}>Nội dung</NavSection>}


          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href="/vocabulary"
                isActive={activePath === '/vocabulary'}
                onClick={(e) => handleNavClick('/vocabulary', e)}
              >
                <FaBookOpen />
                <span>Từ vựng</span>
              </NavLink>
            </NavItem>
          )}

          {userInfo && (
            <NavItem>
              <NavLink
                expand={expand}
                href="/flashcards"
                isActive={activePath.startsWith('/flashcards')}
                onClick={(e) => handleNavClick('/flashcards', e)}
              >
                <BsCollection />
                <span>Bộ từ vựng</span>
              </NavLink>
            </NavItem>
          )}

          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.FLASHCARD_PDF_CREATE}
                isActive={activePath === pathConstants.FLASHCARD_PDF_CREATE}
                onClick={(e) => handleNavClick(pathConstants.FLASHCARD_PDF_CREATE, e)}
              >
                <GiCardPick />
                <span>Tạo Thẻ Từ / Test Vocab</span>
              </NavLink>
            </NavItem>
          )}

          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href="/documents"
                isActive={activePath.startsWith('/documents')}
                onClick={(e) => handleNavClick('/documents', e)}
              >
                <HiOutlineDocumentText />
                <span>Thư viện tài liệu</span>
              </NavLink>
            </NavItem>
          )}

          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.QUESTIONS_MANAGE}
                isActive={activePath === pathConstants.QUESTIONS_MANAGE}
                onClick={(e) => handleNavClick(pathConstants.QUESTIONS_MANAGE, e)}
              >
                <BsFillPatchQuestionFill />
                <span>Câu hỏi</span>
              </NavLink>
            </NavItem>
          )}

          {isTeacherOrAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.EXAMS_MANAGE}
                isActive={activePath === pathConstants.EXAMS_MANAGE}
                onClick={(e) => handleNavClick(pathConstants.EXAMS_MANAGE, e)}
              >
                <FaFileAlt />
                <span>Đề thi</span>
              </NavLink>
            </NavItem>
          )}


          {/* ── HỌC PHÍ & ADMIN ───────────────────────────────── */}
          {isAdmin && <NavSection expand={expand}>Admin</NavSection>}

          {isAdmin && (
            <NavItem>
              <NavLink expand={expand} href="#" onClick={(e) => { e.preventDefault(); toggleDropdown('tuition'); }}>
                <MdAttachMoney />
                <span>Học phí</span>
                <Chevron open={activeDropdown === 'tuition'}><FaChevronDown /></Chevron>
              </NavLink>
              {activeDropdown === 'tuition' && (
                <Dropdown expand={expand}>
                  <DropdownItem
                    onClick={(e: React.MouseEvent) => handleNavClick(pathConstants.TUITION_MANAGE_NEW, e)}
                    style={{
                      color: activePath === pathConstants.TUITION_MANAGE_NEW ? '#bae6fd' : undefined,
                      borderLeftColor: activePath === pathConstants.TUITION_MANAGE_NEW ? '#38bdf8' : undefined,
                    }}
                  >
                    💵 Tính học phí
                  </DropdownItem>
                  <DropdownItem
                    onClick={(e: React.MouseEvent) => handleNavClick(pathConstants.TUITION_STATISTICS, e)}
                    style={{
                      color: activePath === pathConstants.TUITION_STATISTICS ? '#c4b5fd' : undefined,
                      borderLeftColor: activePath === pathConstants.TUITION_STATISTICS ? '#818cf8' : undefined,
                    }}
                  >
                    📊 Thống kê HP
                  </DropdownItem>
                </Dropdown>
              )}
            </NavItem>
          )}

          {isAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.USER_MANAGE}
                isActive={activePath === pathConstants.USER_MANAGE}
                onClick={(e) => handleNavClick(pathConstants.USER_MANAGE, e)}
              >
                <FaUsers />
                <span>Quản lý users</span>
              </NavLink>
            </NavItem>
          )}

          {isAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.IELTS_PRACTICE}
                isActive={activePath === pathConstants.IELTS_PRACTICE}
                onClick={(e) => handleNavClick(pathConstants.IELTS_PRACTICE, e)}
              >
                <FaFlask />
                <span>Luyện thi IELTS</span>
              </NavLink>
            </NavItem>
          )}

          {isAdmin && (
            <NavItem>
              <NavLink
                expand={expand}
                href={pathConstants.BANK_ACCOUNT_MANAGE}
                isActive={activePath === pathConstants.BANK_ACCOUNT_MANAGE}
                onClick={(e) => handleNavClick(pathConstants.BANK_ACCOUNT_MANAGE, e)}
              >
                <FaMoneyBillWave />
                <span>Tài khoản NH</span>
              </NavLink>
            </NavItem>
          )}
        </Nav>

        {/* Footer / Logout */}
        <Footer>
          <NavLink expand={expand} href="#" onClick={onLogout} style={{ borderRadius: 10, margin: 0 }}>
            <FaSignOutAlt />
            <span>Đăng xuất</span>
          </NavLink>
        </Footer>
      </Sidebar>
    </IconContext.Provider>
  );
});

export default Header;